import React from 'react';
import { X, User, Mail, Phone, MapPin, Calendar, Award, Link2, GraduationCap, Building2, Globe, FileText, Briefcase, Clock, Banknote, Video, Mars, Venus, Transgender } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../CommonLoader';
import { getFileURL } from '../../utils/fileURL';
import MasterLabel from '../common/MasterLabel';

const DoctorDetailsModal = ({ isOpen, onClose, doctor, loading }) => {
    const { t } = useTranslation('doctordetailsmodal');

    if (!isOpen) return null;

    const formatDate = (dateString) => {
        if (!dateString) return t('doctordetailsmodal.na');
        let date;
        const str = String(dateString).trim();
        if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) {
            date = new Date(str);
        } else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) {
            const parts = str.split(/[/-]/);
            date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        } else {
            date = new Date(str);
        }
        if (isNaN(date.getTime())) return t('doctordetailsmodal.na');
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getCountryName = (country) => {
        if (!country) return t('doctordetailsmodal.na');
        if (typeof country === 'object') return country.countryName || country.countryname || '';
        return country;
    };

    const getCurrencySymbol = (country) => {
        if (!country) return '';
        const symbol = typeof country === 'object' ? (country.currencySymbol || country.currencyCode || '') : '';
        if (symbol) return symbol;
        const name = getCountryName(country);
        if (name === 'India') return '₹';
        if (name === 'France') return '€';
        return '';
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="doctor-details-modal"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="doctor-details-modal-content"
                    >
                        {/* Header */}
                        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white px-6 py-4 flex justify-between items-center shadow-md" data-testid="doctor-details-modal-header">
                            <h2 className="text-xl sm:text-2xl font-bold" data-testid="doctor-details-modal-title">
                                {t('doctordetailsmodal.title')}
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                                aria-label={t('doctordetailsmodal.close')}
                                data-testid="doctor-details-modal-close-button"
                            >
                                <X className="w-6 h-6" data-testid="doctor-details-modal-close-icon" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 custom-scrollbar" data-testid="doctor-details-modal-body">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12" data-testid="doctor-details-modal-loading">
                                    <CommonLoader />
                                    <span className="text-xl text-[#4ABDBD] font-semibold mt-4" data-testid="doctor-details-modal-loading-text">
                                        {t('doctordetailsmodal.loading')}
                                    </span>
                                </div>
                            ) : doctor ? (
                                <div className="space-y-6" data-testid="doctor-details-modal-data">
                                    {/* Personal Information Card */}
                                    <div className="bg-gradient-to-br from-[#4ABDBD]/5 to-[#3A9B9B]/5 border border-[#4ABDBD]/20 rounded-2xl p-6 shadow-sm" data-testid="doctor-details-personal-info-section">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-2 bg-[#4ABDBD] rounded-lg">
                                                <User className="text-white w-5 h-5" data-testid="doctor-details-personal-info-icon" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-800" data-testid="doctor-details-personal-info-title">
                                                {t('doctordetailsmodal.personal_information')}
                                            </h3>
                                        </div>

                                        <div className="flex flex-col md:flex-row gap-6 items-start">
                                            {/* Avatar */}
                                            <div className="shrink-0">
                                                {doctor.image ? (
                                                    <img
                                                        src={getFileURL(doctor.image)}
                                                        alt={`Dr. ${doctor.name} ${doctor.surname}`}
                                                        className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                                                    />
                                                ) : (
                                                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] flex items-center justify-center border-4 border-white shadow-lg">
                                                        <User className="text-white w-16 h-16" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Details Grid */}
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase">
                                                        {t('doctordetailsmodal.full_name')}
                                                    </p>
                                                    <p className="text-base font-bold text-gray-900">
                                                        {doctor.name} {doctor.surname}
                                                    </p>
                                                </div>

                                                <div className="space-y-1">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Mail className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('doctordetailsmodal.email')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900 break-all">
                                                        {doctor.email || t('doctordetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Phone className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('doctordetailsmodal.phone')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900">
                                                        {doctor.phone || t('doctordetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Award className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('doctordetailsmodal.license_number')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900">
                                                        {doctor.licenseNumber || t('doctordetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        {doctor.gender?.toLowerCase() === 'male' && <Mars className="w-3.5 h-3.5 text-[#4ABDBD]" />}
                                                        {doctor.gender?.toLowerCase() === 'female' && <Venus className="w-3.5 h-3.5 text-[#4ABDBD]" />}
                                                        {doctor.gender?.toLowerCase() === 'other' && <Transgender className="w-3.5 h-3.5 text-[#4ABDBD]" />}
                                                        {t('doctordetailsmodal.gender')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900 capitalize">
                                                        {doctor.gender ? t(`common.gender.${doctor.gender.toLowerCase()}`, doctor.gender) : t('doctordetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Calendar className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('doctordetailsmodal.date_of_birth')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900">
                                                        {doctor.dob ? formatDate(doctor.dob) : t('doctordetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Globe className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('doctordetailsmodal.country')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900">
                                                        {getCountryName(doctor.country) || t('doctordetailsmodal.na')}
                                                    </p>
                                                </div>

                                                <div className="space-y-1">
                                                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                                                        <Calendar className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                        {t('doctordetailsmodal.created_at')}
                                                    </p>
                                                    <p className="text-base font-medium text-gray-900">
                                                        {formatDate(doctor.created_at)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        {doctor.description && (
                                            <div className="mt-6 pt-6 border-t border-gray-200">
                                                <p className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1">
                                                    <FileText className="w-3.5 h-3.5 text-[#4ABDBD]" />
                                                    {t('doctordetailsmodal.description')}
                                                </p>
                                                <p className="text-sm font-medium text-gray-700 leading-relaxed">
                                                    {doctor.description}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Specialities with Appointment Types */}
                                    {doctor.appointmentSpeciality && doctor.appointmentSpeciality.length > 0 && (
                                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-6 shadow-sm" data-testid="doctor-details-specialities-section">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 bg-blue-500 rounded-lg">
                                                    <Briefcase className="text-white w-5 h-5" data-testid="doctor-details-specialities-icon" />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800" data-testid="doctor-details-specialities-title">
                                                    {t('doctordetailsmodal.specialities_appointment_types')}
                                                </h3>
                                            </div>
                                            <div className="space-y-4">
                                                {doctor.appointmentSpeciality.map((spec, index) => (
                                                    <div
                                                        key={index}
                                                        className="bg-white rounded-xl p-5 shadow-sm border border-blue-200"
                                                        data-testid={`doctor-details-speciality-card-${index}`}
                                                    >
                                                        <div className="flex items-center gap-2 mb-4">
                                                            <Award className="w-5 h-5" style={{ color: spec.color || '#4ABDBD' }} />
                                                            <h4 className="text-lg font-bold text-gray-900">
                                                                <MasterLabel type="masterSpecialty" id={spec.specialtyId} field="name" fallback={spec.name} />
                                                            </h4>
                                                        </div>

                                                        {spec.appointmentTypes && spec.appointmentTypes.length > 0 && (
                                                            <div className="space-y-3">
                                                                <p className="text-xs font-semibold text-gray-500 uppercase">
                                                                    {t('doctordetailsmodal.appointment_types')}
                                                                </p>
                                                                <div className="grid grid-cols-1 gap-3">
                                                                    {spec.appointmentTypes.map((type, typeIndex) => (
                                                                        <div
                                                                            key={typeIndex}
                                                                            className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-100"
                                                                        >
                                                                            <div className="flex items-start justify-between gap-4 mb-3">
                                                                                <p className="text-sm font-bold text-gray-900">
                                                                                    {type.type}
                                                                                </p>
                                                                            </div>
                                                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Clock className="w-4 h-4 text-blue-500 shrink-0" />
                                                                                    <div>
                                                                                        <p className="text-xs text-gray-500">{t('doctordetailsmodal.duration')}</p>
                                                                                        <p className="text-sm font-bold text-gray-900">
                                                                                            {type.duration || t('doctordetailsmodal.na')}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Banknote className="w-4 h-4 text-blue-500 shrink-0" />
                                                                                    <div>
                                                                                        <p className="text-xs text-gray-500">{t('doctordetailsmodal.price')}</p>
                                                                                        <p className="text-sm font-bold text-gray-900">
                                                                                            {getCurrencySymbol(doctor.country)}{type.price || t('doctordetailsmodal.na')}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Video className="w-4 h-4 text-blue-500 shrink-0" />
                                                                                    <div>
                                                                                        <p className="text-xs text-gray-500">{t('doctordetailsmodal.mode')}</p>
                                                                                        <p className="text-sm font-bold text-gray-900">
                                                                                            {type.virtual ? t('doctordetailsmodal.onsite_visio') : t('doctordetailsmodal.onsite')}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                                                                                    <div>
                                                                                        <p className="text-xs text-gray-500">{t('doctordetailsmodal.type_id')}</p>
                                                                                        <p className="text-xs font-mono font-bold text-gray-900">
                                                                                            {type.typeId || t('doctordetailsmodal.na')}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Addresses */}
                                    {doctor.address && doctor.address.length > 0 && (
                                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 shadow-sm" data-testid="doctor-details-addresses-section">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 bg-green-500 rounded-lg">
                                                    <MapPin className="text-white w-5 h-5" data-testid="doctor-details-addresses-icon" />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800" data-testid="doctor-details-addresses-title">
                                                    {t('doctordetailsmodal.addresses')}
                                                </h3>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {doctor.address.map((addr, index) => (
                                                    <div
                                                        key={index}
                                                        className="bg-white rounded-xl p-5 shadow-sm border border-green-200"
                                                        data-testid={`doctor-details-address-card-${index}`}
                                                    >
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <Building2 className="w-5 h-5 text-green-500" />
                                                            <p className="text-base font-bold text-gray-900">
                                                                {addr.addressType}
                                                            </p>
                                                        </div>
                                                        <div className="space-y-2 text-sm font-medium text-gray-700">
                                                            <p className="flex items-start gap-2">
                                                                <MapPin className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                                                <span>{addr.addressLine1 || '-'}</span>
                                                            </p>
                                                            {addr.addressLine2 && (
                                                                <p className="pl-6 text-gray-600">
                                                                    {addr.addressLine2}
                                                                </p>
                                                            )}
                                                            <p className="pl-6">
                                                                {addr.city || '-'}, {addr.zipCode || '-'}
                                                            </p>
                                                            <p className="pl-6 font-bold">
                                                                <MasterLabel
                                                                    type="masterCountry"
                                                                    id={typeof addr.country === 'object' ? addr.country?.countryId : null}
                                                                    field="countryName"
                                                                    fallback={typeof addr.country === 'object' ? (addr.country?.countryName || addr.country?.countryname || '-') : (addr.country || '-')}
                                                                />
                                                            </p>
                                                        </div>
                                                        {addr.specialty && addr.specialty.length > 0 && (
                                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                                                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('doctordetailsmodal.specialties')}</p>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {addr.specialty.map((spec, specIndex) => (
                                                                        <span
                                                                            key={specIndex}
                                                                            className="px-2 py-1 rounded text-xs font-medium text-white"
                                                                            style={{ backgroundColor: spec.color || '#4ABDBD' }}
                                                                        >
                                                                            <MasterLabel type="masterSpecialty" id={spec.specialtyId} field="name" fallback={spec.name} />
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Availability */}
                                    {doctor.availability && doctor.availability.length > 0 && (
                                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-6 shadow-sm" data-testid="doctor-details-availability-section">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 bg-purple-500 rounded-lg">
                                                    <Calendar className="text-white w-5 h-5" data-testid="doctor-details-availability-icon" />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800" data-testid="doctor-details-availability-title">
                                                    {t('doctordetailsmodal.availability')}
                                                </h3>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {doctor.availability.filter(avail => avail.isActive).map((avail, index) => (
                                                    <div
                                                        key={index}
                                                        className="bg-white rounded-xl p-4 shadow-sm border border-purple-200"
                                                        data-testid={`doctor-details-availability-card-${index}`}
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-sm font-bold text-gray-900">
                                                                {avail.day}
                                                            </p>
                                                            {avail.isActive && (
                                                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                                                    {t('doctordetailsmodal.active')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="space-y-1 text-sm text-gray-700">
                                                            <p className="flex items-center gap-2">
                                                                <Clock className="w-3.5 h-3.5 text-purple-500" />
                                                                <span>{avail.start} - {avail.end}</span>
                                                            </p>
                                                            {avail.breaks && avail.breaks.length > 0 && (
                                                                <div className="pl-5 text-xs text-gray-500">
                                                                    {t('doctordetailsmodal.breaks')}: {avail.breaks.map(b => `${b.breakStart}-${b.breakEnd}`).join(', ')}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Certifications */}
                                    {doctor.certification && doctor.certification.length > 0 && (
                                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-sm" data-testid="doctor-details-certifications-section">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 bg-amber-500 rounded-lg">
                                                    <GraduationCap className="text-white w-5 h-5" data-testid="doctor-details-certifications-icon" />
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-800" data-testid="doctor-details-certifications-title">
                                                    {t('doctordetailsmodal.certifications')}
                                                </h3>
                                            </div>
                                            <div className="space-y-3">
                                                {doctor.certification.map((cert, index) => (
                                                    <div
                                                        key={index}
                                                        className="bg-white rounded-xl p-4 shadow-sm border border-amber-200"
                                                        data-testid={`doctor-details-certification-card-${index}`}
                                                    >
                                                        <h4 className="text-base font-bold text-gray-900" data-testid={`doctor-details-certification-title-${index}`}>
                                                            {cert.title || t('doctordetailsmodal.na')}
                                                        </h4>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12" data-testid="doctor-details-modal-no-data">
                                    <p className="text-gray-500 text-lg" data-testid="doctor-details-modal-no-data-text">
                                        {t('doctordetailsmodal.no_data')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default DoctorDetailsModal;

